// Общее описание строк таблицы сравнения: используется панелью сравнения
// и публичной страницей поездки (src/share/SharedTripPage).
import type { CurrencyDef, VariantTotals } from '../../types';
import { formatDuration, formatMoney } from '../../core';
import type { Translate } from '../../i18n';

export interface CompareMetric {
  key: string;
  /** ключ словаря — подпись переводится на месте отрисовки */
  labelKey: string;
  getValue(t: VariantTotals): number;
  /**
   * currencies нужен денежным строкам (символ валюты), tr — временным
   * (суффиксы часов и минут переводятся вместе с интерфейсом).
   */
  format(
    value: number,
    t: VariantTotals,
    currencies: readonly CurrencyDef[],
    tr: Translate,
  ): string;
  /** Имеет ли смысл подсвечивать минимум в этой строке ("Ночей" — нет). */
  highlight: boolean;
}

export const COMPARE_METRICS: readonly CompareMetric[] = [
  {
    key: 'total',
    labelKey: 'compare.total',
    getValue: (t) => t.totalMinor,
    format: (v, t, currencies) => formatMoney(v, t.currency, currencies),
    highlight: true,
  },
  {
    key: 'totalWithBaggage',
    labelKey: 'compare.totalWithBaggage',
    getValue: (t) => t.totalWithBaggageMinor,
    format: (v, t, currencies) => formatMoney(v, t.currency, currencies),
    highlight: true,
  },
  {
    key: 'flightTime',
    labelKey: 'compare.flightTime',
    getValue: (t) => t.flightTimeMin,
    format: (v, _t, _c, tr) => formatDuration(v, tr),
    highlight: true,
  },
  {
    key: 'doorToDoor',
    labelKey: 'compare.doorToDoor',
    getValue: (t) => t.doorToDoorMin,
    format: (v, _t, _c, tr) => formatDuration(v, tr),
    highlight: true,
  },
  {
    key: 'nights',
    labelKey: 'compare.nights',
    getValue: (t) => t.nights,
    format: (v) => String(v),
    highlight: false,
  },
  {
    key: 'warnings',
    labelKey: 'compare.warnings',
    getValue: (t) => t.warnings.length,
    format: (v) => String(v),
    highlight: true,
  },
];

/** id вариантов, у которых в этой строке минимальное (лучшее) значение. Пусто, если < 2 вариантов. */
export function computeBestSet(totals: readonly VariantTotals[], metric: CompareMetric): Set<string> {
  const best = new Set<string>();
  if (!metric.highlight || totals.length < 2) return best;
  const values = totals.map((t) => metric.getValue(t));
  const min = Math.min(...values);
  totals.forEach((t, i) => {
    if (values[i] === min) best.add(t.variantId);
  });
  return best;
}

/** Максимальный уровень предупреждений варианта — для окраски числа в строке "Предупреждений". */
export function maxWarningLevel(t: VariantTotals): 'error' | 'warn' | null {
  let hasWarn = false;
  for (const w of t.warnings) {
    if (w.level === 'error') return 'error';
    hasWarn = true;
  }
  return hasWarn ? 'warn' : null;
}
